import {BreadCrumb, Dictionary, ProductTypeTaxonomyTerm} from "@/types/hygiena-types";
import {MutableRefObject, useContext, useEffect, useState} from "react";
import {DictionaryContext} from "@/context/dictionary-context";
import {buildBreadCrumbs} from "@/lib/breadcrumbs";
import {MediaImage} from "@/components/templates/media/media--image";
import {HERO_IMAGE_STYLE} from "@/types/image-styles";
import {HERO_IMAGE_SIZES, HERO_MAX_WIDTH, PlaceHolderHeroImage} from "@/components/organisms/hero";
import {FrostedGlassContainer} from "@/components/atoms/gradient-container";
import {Link} from "@/components/atoms/link";
import {FormattedText} from "@/components/formatted-text";
import {Button} from "@/components/atoms/button";
import {stripOuterParagraph} from "@/lib/strip-outer-paragraph";
import {absoluteURL} from "@/lib/absolute-url";
import {CONTACT_PORTAL_ID} from "@/lib/constants";

/**
 * Product Type/Product Name Taxonomy Page Hero.
 * @param taxonomy_term
 *   A Product Type/Product Name taxonomy term.
 * @param scrollRef
 *   A ref to the section to scroll to on button click.
 * @constructor
 */
export function ProductTypeHero ({
                                   taxonomy_term,
                                   scrollRef
                                 }: {
  taxonomy_term: ProductTypeTaxonomyTerm
  scrollRef: MutableRefObject<any>|null
}) {
  const t = useContext<Dictionary>(DictionaryContext);
  const breadcrumbs = [] as BreadCrumb[];
  const heroButtonText = taxonomy_term?.field_product_type_hero_btn_text ?? `${t?.all?.see_testing_solutions}`;
  const image = taxonomy_term?.field_product_type_image;
  const [smallScreen, setSmallScreen] = useState<boolean>();
  const [beginVideo, setBeginVideo] = useState<boolean>(false);
  const video = taxonomy_term?.field_hero_video;

  function jumpToContactus() {
    const form = document.getElementById(CONTACT_PORTAL_ID);
    if (form) {
      form.scrollIntoView({behavior: 'smooth'})
    }
  }

  /**
   * Scrolls to the solutions title if present.
   */
  function scrollToRef() {
    if (!scrollRef?.current?.scrollIntoView) return;
    const current = scrollRef.current as HTMLHeadingElement;
    current.scrollIntoView({behavior: 'smooth'})
  }

  if (taxonomy_term?.parent) {
    buildBreadCrumbs(taxonomy_term?.parent[0], breadcrumbs);
  }

  // We use the video URL if there is only one video being passed, if the video is an array, we
  // apply a fader.
  let videoFile;
  if (Array.isArray(video) && video?.length === 1) {
    videoFile = video?.[0]?.field_media_file;
  }
  else if (!Array.isArray(video) && video?.field_media_file) {
    videoFile = video.field_media_file;
  }
  const videoUrl = videoFile?.uri?.url
    ? absoluteURL(videoFile?.uri?.url)
    : undefined;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const setSize = () => setSmallScreen(window.innerWidth < 768);
    setSize();
    const loadVideo = () => !beginVideo && setBeginVideo(true);
    window.addEventListener("resize", setSize);
    window.addEventListener("mousemove", loadVideo);

    return () => {
      window.removeEventListener("resize", setSize);
      window.removeEventListener("mousemove", loadVideo);
    }
  }, []);

  return (
    <div className={"relative 2xl:mx-auto flex mx-auto py-12 max-w-[1920px] overflow-hidden md:py-0 md:h-[480px] lg:h-[720px]"}>
      {!videoUrl && image ? (
        <MediaImage
          priority
          loading={"eager"}
          media={image}
          imageStyle={HERO_IMAGE_STYLE}
          key={`product-type-hero-image--${image.id}`}
          sizes={HERO_IMAGE_SIZES}
          className={"absolute inset-0"} style={{objectFit: "cover", height: "100%", width: "100%", maxWidth: HERO_MAX_WIDTH, margin: "auto"}}/>
      ) : (
        <PlaceHolderHeroImage altText={taxonomy_term?.name} />
      )}
      {videoUrl && (
        <div className="h-full w-full absolute">
          {(image?.image || image?.bf_image) && (
            <MediaImage
              priority
              loading={"eager"}
              imageStyle={HERO_IMAGE_STYLE}
              media={image}
              key={`hero-image--${image.id}`}
              sizes={HERO_IMAGE_SIZES}
              className={"absolute inset-0 overflow-hidden"} style={{objectFit: "cover", height: "100%", width: "100%", maxWidth: HERO_MAX_WIDTH, margin: "auto"}}/>
          )}
          {beginVideo && !smallScreen && (
            <video muted loop autoPlay className="max-md:hidden object-cover h-full w-full absolute">
              <source src={videoUrl} type="video/mp4"/>
            </video>
          )}
        </div>
      )}
      <div className="container mx-0 md:mx-8 lg:mx-auto py-12 md:py-0 min-h-fit flex lg:!justify-start items-center">
        <FrostedGlassContainer className="flex flex-col rounded-none md:sm:rounded-lg p-8 md:p-12 lg:p-16 w-full lg:!max-w-4xl">
          <div className="text-primary font-semibold mb-4">
            {breadcrumbs.map((item, index) => {
              if (item.url) {
                return (
                  <div className="inline" key={`breadcrumb--${item.id}`}>
                    <Link href={item.url ?? "/"} className="hover:text-white transition-all">{item.name}</Link>
                    {index + 1 < breadcrumbs.length && (<span className="px-1.5">/</span>)}
                  </div>
                )
              }
            })}
          </div>
          <h1>
            {
              taxonomy_term?.field_product_type_display_title?.processed
                ? (<FormattedText processed={taxonomy_term.field_product_type_display_title.processed} options={stripOuterParagraph}/>)
                : (taxonomy_term.name)
            }
          </h1>
          <div className="mb-12 font-light tracking-snug leading-8 md:text-xl text-black">
            {taxonomy_term?.description?.value && (
              <FormattedText processed={taxonomy_term.description.value}/>
            )}
          </div>
          <div>
            {scrollRef && (
              <Button callback={scrollToRef} hover={{color: "primary"}} position={"right"} icon={"arrow-down"} height={20} width={20}>{heroButtonText}</Button>
            )}
          </div>
          <div className="mt-3">
            <Button callback={jumpToContactus} className="py-1.5 px-1 bg-white w-full h-full" color={"primary"} hover={{color: "primary"}} invert={true}>{`${t?.all?.request_more_info}`}</Button>
          </div>
        </FrostedGlassContainer>
      </div>
    </div>
  );
}

